import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

// Misma clave que lee el script anti-flash de index.html — si se cambia acá
// hay que cambiarla también ahí.
const STORAGE_KEY = 'redeapp-theme';

const ThemeProviderContext = createContext<ThemeProviderState | null>(null);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // localStorage bloqueado (modo privado estricto): arranca en 'system'
  }
  return 'system';
}

function applyTheme(theme: Theme): void {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  // Aplica la clase "dark" en <html> cada vez que cambia la preferencia.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // En modo 'system', sigue los cambios de prefers-color-scheme en vivo
  // (ej. el usuario cambia el tema del SO sin recargar la app).
  useEffect(() => {
    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    function onChange(): void {
      applyTheme('system');
    }
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [theme]);

  function setTheme(next: Theme): void {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage bloqueado: se pierde solo la persistencia entre sesiones
    }
    setThemeState(next);
  }

  return (
    <ThemeProviderContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeProviderState {
  const ctx = useContext(ThemeProviderContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de ThemeProvider');
  return ctx;
}
